import { Button } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { SubscriptionService } from '@affine/core/modules/cloud';
import { SubscriptionRecurring } from '@affine/graphql';
import { i18nTime, useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';

import type { Tariffs } from '../plans/tariff-plans';
import { CardNameLabelRow } from './card-name-label-row';
import * as styles from './style.css';

/**
 * Fork billing: current yookassa tariff with price, period and the next
 * charge / expiration date.
 */
export const TariffPlanCard = ({
  tariffs,
  gotoCloudPlansSetting,
}: {
  tariffs: Tariffs;
  gotoCloudPlansSetting: () => void;
}) => {
  const t = useI18n();
  const subscription = useLiveData(
    useService(SubscriptionService).subscription.pro$
  );
  const tier = tariffs.tiers.find(tier => tier.id === subscription?.tier);
  const yearly = subscription?.recurring === SubscriptionRecurring.Yearly;
  const amount = tier ? (yearly ? tier.yearlyPrice : tier.monthlyPrice) : 0;
  const date = (value: string) =>
    i18nTime(value, { absolute: { accuracy: 'day' } });

  const desc = !subscription
    ? t['com.affine.payment.tariffs.current']({ plan: 'Free' })
    : subscription.canceledAt && subscription.end
      ? t['com.affine.payment.tariffs.current-canceled']({
          plan: tier?.name ?? 'Pro',
          end: date(subscription.end),
        })
      : t['com.affine.payment.tariffs.current-renews']({
          plan: tier?.name ?? 'Pro',
          end: subscription.nextBillAt ? date(subscription.nextBillAt) : '',
        });

  return (
    <div className={styles.planCard}>
      <div className={styles.currentPlan}>
        <SettingRow
          spreadCol={false}
          name={
            <CardNameLabelRow
              cardName={t['com.affine.payment.billing-setting.current-plan']()}
              status={subscription?.status}
            />
          }
          desc={
            <>
              <p>{desc}</p>
              <Button
                className={styles.planAction}
                variant="primary"
                onClick={gotoCloudPlansSetting}
              >
                {subscription
                  ? t['com.affine.payment.billing-setting.change-plan']()
                  : t['com.affine.payment.billing-setting.upgrade']()}
              </Button>
            </>
          }
        />
      </div>
      <p className={styles.planPrice}>
        {(amount / 100).toLocaleString('ru-RU')} ₽
        <span className={styles.billingFrequency}>
          /
          {yearly
            ? t['com.affine.payment.billing-setting.year']()
            : t['com.affine.payment.billing-setting.month']()}
        </span>
      </p>
    </div>
  );
};
